import { ExcelGridColumn, ExcelGridRow } from "../../shared/excel-grid/models/excel-grid.models";

export const ADMIN_UWHC_PROVIDER_FIELDS = [
  "COMPANY",
  "PLACE",
  "COUNTRY",
  "REGION",
  "CITY",
  "PORT_NAME",
  "SCREENED",
  "PREFERRED",
  "VERTICAL_SIDES_CLEANING",
  "FLAT_BOTTOM_CLEANING",
  "PROPELLER_CLEANING",
  "SEA_CHEST_CLEANING_INTERNALLY",
  "SEA_CHEST_CLEANING_EXTERNALLY",
  "RUDDER_CLEANING",
  "BOW_THRUSTER_GRID_CLEANING",
  "BOW_THRUSTER_TUNNEL_CLEANING",
  "BILGE_KEEL_CLEANING",
  "ABOVE_WATERLINE_CLEANING",
  "UW_INSPECTIONS",
  "UW_REPAIRS",
  "CLEANING_TO_AUS_NZ_STANDARDS",
  "DIVER_EMPLOYED_BRUSH_CARTS",
  "DIVER_EMPLOYED_POLISHERS",
  "HAND_SCRAPERS",
  "HIGH_PRESSURE_WATERJETS",
  "ROV_FOR_UWHC",
  "ROV_FOR_UW_INSPECTIONS",
  "HULLWIPER",
  "ADDITIONAL_INFO",
  "REGULATIONS",
  "SERVICE_24_7",
  "PHOTOS_PROVIDED",
  "VIDEOS_PROVIDED",
  "REPORT_PROVIDED",
  "PHONE",
  "EMAIL",
] as const;

export type AdminUwhcProviderField = (typeof ADMIN_UWHC_PROVIDER_FIELDS)[number];
export type AdminUwhcProviderPayload = Partial<Record<AdminUwhcProviderField, string | null>>;

export type AdminUwhcProviderRow = ExcelGridRow &
  AdminUwhcProviderPayload & {
    ID?: number | null;
    PROVIDER_UID?: string | null;
    START_TS?: string | null;
    END_TS?: string | null;
    CURR_IND?: string | null;
    isDraft?: boolean;
  };

export interface AdminUwhcProviderListResponse {
  rows: AdminUwhcProviderRow[];
}

export interface AdminUwhcProviderMutationResponse {
  row: AdminUwhcProviderRow;
}

export interface AdminUwhcProviderDeleteResponse {
  success: true;
  providerUid: string;
}

export const ADMIN_UWHC_SERVICE_FIELDS: AdminUwhcProviderField[] = [
  "SCREENED",
  "PREFERRED",
  "VERTICAL_SIDES_CLEANING",
  "FLAT_BOTTOM_CLEANING",
  "PROPELLER_CLEANING",
  "SEA_CHEST_CLEANING_INTERNALLY",
  "SEA_CHEST_CLEANING_EXTERNALLY",
  "RUDDER_CLEANING",
  "BOW_THRUSTER_GRID_CLEANING",
  "BOW_THRUSTER_TUNNEL_CLEANING",
  "BILGE_KEEL_CLEANING",
  "ABOVE_WATERLINE_CLEANING",
  "UW_INSPECTIONS",
  "UW_REPAIRS",
  "CLEANING_TO_AUS_NZ_STANDARDS",
  "DIVER_EMPLOYED_BRUSH_CARTS",
  "DIVER_EMPLOYED_POLISHERS",
  "HAND_SCRAPERS",
  "HIGH_PRESSURE_WATERJETS",
  "ROV_FOR_UWHC",
  "ROV_FOR_UW_INSPECTIONS",
  "HULLWIPER",
  "SERVICE_24_7",
  "PHOTOS_PROVIDED",
  "VIDEOS_PROVIDED",
  "REPORT_PROVIDED",
];

export const ADMIN_UWHC_TEXT_AREA_FIELDS: AdminUwhcProviderField[] = ["ADDITIONAL_INFO", "REGULATIONS"];

export const ADMIN_UWHC_TEXT_FIELDS: AdminUwhcProviderField[] = ADMIN_UWHC_PROVIDER_FIELDS.filter(
  (field) => !ADMIN_UWHC_SERVICE_FIELDS.includes(field) && !ADMIN_UWHC_TEXT_AREA_FIELDS.includes(field),
);

export const ADMIN_UWHC_PROVIDER_COLUMNS: ExcelGridColumn[] = [
  {
    key: "PROVIDER_UID",
    label: "Provider",
    editable: false,
    minWidth: 84,
    maxWidth: 96,
    renderCell: getProviderUidColumnValue,
  },
  { key: "COMPANY", label: "Company", minWidth: 180, maxWidth: 300 },
  { key: "PLACE", label: "Place", filterMatch: "wholeWords", minWidth: 140, maxWidth: 240 },
  { key: "COUNTRY", label: "Country", filterMatch: "wholeWords", minWidth: 130, maxWidth: 220 },
  { key: "REGION", label: "Region", filterMatch: "wholeWords", minWidth: 130, maxWidth: 220 },
  { key: "CITY", label: "City", filterMatch: "wholeWords", minWidth: 130, maxWidth: 220 },
  { key: "PORT_NAME", label: "Port", filterMatch: "wholeWords", minWidth: 160, maxWidth: 260 },
  ...ADMIN_UWHC_SERVICE_FIELDS.map((field) => ({
    key: field,
    label: getProviderColumnLabel(field),
    serviceColumn: true,
    minWidth: 118,
    maxWidth: 220,
  })),
  { key: "ADDITIONAL_INFO", label: "Additional Info", minWidth: 280, maxWidth: 520 },
  { key: "REGULATIONS", label: "Regulations", minWidth: 260, maxWidth: 520 },
  { key: "PHONE", label: "Phone", minWidth: 150, maxWidth: 260 },
  { key: "EMAIL", label: "Email", minWidth: 190, maxWidth: 320 },
];

function getProviderColumnLabel(field: AdminUwhcProviderField): string {
  const labels: Partial<Record<AdminUwhcProviderField, string>> = {
    SERVICE_24_7: "24/7 Service",
    UW_INSPECTIONS: "UW Inspections",
    UW_REPAIRS: "UW Repairs",
    ROV_FOR_UWHC: "ROV UWHC",
    ROV_FOR_UW_INSPECTIONS: "ROV UW Inspections",
  };

  return labels[field] ?? field.replaceAll("_", " ").replace(/\b\w/g, (letter) => letter.toUpperCase());
}

function getProviderUidColumnValue(row: ExcelGridRow): string {
  const providerUid = row.PROVIDER_UID;

  if (typeof providerUid !== "string") {
    return "";
  }

  return providerUid.slice(0, 8);
}

export const getAdminUwhcProviderColumnLabel = getProviderColumnLabel;

export type AdminUwhcMetricTone = "success" | "warning" | "danger" | "neutral" | "accent";

export interface AdminUwhcMetric {
  key: string;
  label: string;
  value: string | number;
  caption?: string;
  tone: AdminUwhcMetricTone;
}

export interface AdminUwhcBreakdownItem {
  label: string;
  value: number;
  caption?: string;
}

export interface AdminUwhcLocationPin {
  key: string;
  label: string;
  locationLabel: string;
  x: number;
  y: number;
  count: number;
  matchLevel: "place" | "country" | "unknown";
}

export interface AdminUwhcCoverageStat {
  key: string;
  label: string;
  count: number;
  rate: number;
}

export interface AdminUwhcQualitySignal {
  key: string;
  label: string;
  value: number;
  tone: AdminUwhcMetricTone;
}

export interface AdminUwhcMonitoringStats {
  metrics: AdminUwhcMetric[];
  topCountries: AdminUwhcBreakdownItem[];
  topRegions: AdminUwhcBreakdownItem[];
  topServices: AdminUwhcCoverageStat[];
  contactCoverage: AdminUwhcCoverageStat[];
  qualitySignals: AdminUwhcQualitySignal[];
  locationPins: AdminUwhcLocationPin[];
  unmatchedLocations: number;
  updatedAt: string;
}
